import {
  AdditionalSupplement,
  ChemicalConstituent,
  DeepwellWasteProfile,
  DeepwellWasteProfileMeta,
  LabAnalyticalAttachment,
  SafetyDataSheet,
  WasteProfile,
} from '../interfaces';
import { WasteProfileModel } from './waste-profile.model';

type CompositionTypes = 'analysis' | 'safety-data-sheet' | 'process-knowledge';
export class DeepwellWasteProfileModel
  extends WasteProfileModel
  implements DeepwellWasteProfile
{
  static readonly DEFAULT_CONFIG: DeepwellWasteProfile = {
    ...WasteProfileModel.DEFAULT_CONFIG,
    type: 'deepwell',
    meta: {
      sections: {
        generator: {
          completed: false,
          valid: false,
        },
        billing: {
          completed: false,
          valid: false,
        },
        deepwellDestination: {
          completed: false,
          valid: false,
        },
        deepwellWasteStream: {
          completed: false,
          valid: false,
        },
        regulatory: {
          completed: false,
          valid: false,
        },
        shippingPacking: {
          completed: false,
          valid: false,
        },
        deepwellCharacteristics: {
          completed: false,
          valid: false,
        },
        chemicalComposition: {
          completed: false,
          valid: false,
        },
        attachments: {
          completed: false,
          valid: false,
        },
        chemicalProperties: {
          completed: false,
          valid: false,
        },
        certification: {
          completed: false,
          valid: false,
        },
        checkList: {
          completed: false,
          valid: false,
        },
        signature: {
          completed: false,
          valid: false,
        },
      },
    },
    wasteType: null,
    isTsdfConsolidation: null,
    container: {
      type: null,
      size: null,
      unitOfMeasure: '',
    },
    annualVolume: {
      amount: null,
      unitOfMeasure: null,
    },
    frequency: null,
    frequencyOther: '',
    phRange: null,
    phLow: null,
    phHigh: null,
    phExact: null,
    flashPoint: null,
    isBenzeneRegulated: null,
    isSuperfundCercla: null,
    stateWasteCodes: [],
    rcraWasteCodes: [],
    isDotHazmat: null,
    unOrNaNumber: '',
    packagingGroup: null,
    hazardousClasses: [],
    dotProperShippingName: '',
    dotNosHazardousConstituents: '',
    ergNumber: '',
    hasReportableQuantity: null,
    reportableQuantityBasis: '',
    reportableQuantityValuePounds: null,
    isMarinePollutant: null,
    marinePollutantDescription: '',
    emergencyContact24Hour: '',
    primaryColor: '',
    layers: null,
    odorStrength: null,
    odorDescription: '',
    dryWeightTonPerGallon: null,
    vocPpm: null,
    percentSuspendedSolids: '',
    specificGravity: '',
    specificGravityExact: null,
    totalAlkalinity: null,
    flashPointTestMethod: null,
    containsAmmonia: null,
    labAnalyticalAttachments: [],
    safetyDataSheets: [],
    additionalSupplements: [],
    constituents: [],
    compositionDeterminedBy: [],
    chemicalProperties: {
      arsenicPpm: null,
      bariumPpm: null,
      cadmiumPpm: null,
      chromiumPpm: null,
      leadPpm: null,
      mercuryPpm: null,
      seleniumPpm: null,
      silverPpm: null,
      tphPpm: null,
      antimonyPpm: null,
      berylliumPpm: null,
      nickelPpm: null,
      chromiumIVPpm: null,
      cobaltPpm: null,
      copperPpm: null,
      molybdenumPpm: null,
      thalliumPpm: null,
      vanadiumPpm: null,
      zincPpm: null,
      bromidePpm: null,
      calciumPpm: null,
      chloridePpm: null,
      fluoridePpm: null,
      iodinePpm: null,
      ironPpm: null,
      magnesiumPpm: null,
      potassiumPpm: null,
      sodiumPpm: null,
      sulfurPpm: null,
    },

    // chemical certifications step
    chemicalCertifications: {
      pcbPpm: null,
      benzenePpm: null,
      dioxinsPpm: null,
      sulfidesPpm: null,
      cyanidesPpm: null,
      oxidizerPpm: null,
      seleniumPpm: null,
      silverPpm: null,
      hasPathogens: null,
      pathogensDescription: '',
      hasPesticides: null,
      pesticidesDescription: '',
      hasPolymerizes: null,
      polymerizesDescription: '',
      hasRadioactives: null,
      radioactivesDescription: '',
      hasWaterReactives: null,
      waterReactivesDescription: '',
      hasOtherReactives: null,
      otherReactivesDescription: '',
      hasPentachlorophenol: null,
      pentachlorophenolDescription: '',
      hasOSHACarcinogen: null,
      oshaCarcinogenDescription: '',
    },
  };

  // other data
  readonly type: 'deepwell';
  readonly meta: DeepwellWasteProfileMeta;

  // waste stream step
  readonly wasteType:
    | 'non-hazardous-non-industrial'
    | 'non-hazardous-industrial-state-regulated'
    | 'hazardous'
    | 'pollution-control';
  readonly isTsdfConsolidation: boolean;
  readonly container: {
    readonly type: 'bulk-tanker' | 'vac-truck' | 'rail-car' | 'drums' | 'totes';
    readonly size: number;
    readonly unitOfMeasure: string; // enum tbd
  };
  readonly annualVolume: {
    readonly amount: number;
    readonly unitOfMeasure: 'gallons' | 'tons' | 'pounds';
  };
  readonly frequency:
    | 'one-time'
    | 'daily'
    | 'weekly'
    | 'monthly'
    | 'quarterly'
    | 'other';
  readonly frequencyOther: string;

  // fed/state reg step
  readonly isBenzeneRegulated: boolean;
  readonly isSuperfundCercla: boolean;
  readonly stateWasteCodes: string[]; // TBD: Reference data
  readonly rcraWasteCodes: string[]; // TBD: Reference data

  // shipping/packing step
  readonly isDotHazmat: boolean;
  readonly unOrNaNumber: string; // Must be prefixed with 'UN' or 'NA', fixed length of 6
  readonly packagingGroup: 'I' | 'II' | 'III';
  readonly hazardousClasses: string[];
  readonly dotProperShippingName: string;
  readonly dotNosHazardousConstituents: string;
  readonly ergNumber: string;
  readonly hasReportableQuantity: boolean;
  readonly reportableQuantityBasis: string; // only required when `hasReportableQuantity: true`
  readonly reportableQuantityValuePounds: number; // only required when `hasReportableQuantity: true`
  readonly isMarinePollutant: boolean;
  readonly marinePollutantDescription: string; // only required when `isMarinePollutant: true`
  readonly emergencyContact24Hour: string;

  // characteristics step
  readonly primaryColor: string;
  readonly layers: 'multi-layered' | 'bi-layered' | 'single-phase';
  readonly odorStrength: 'none' | 'slight' | 'strong';
  readonly odorDescription: string; // required when is not `odorStrength: 'none'`
  readonly dryWeightTonPerGallon: number;
  readonly vocPpm: number;
  readonly percentSuspendedSolids: string;
  readonly specificGravity: string;
  readonly specificGravityExact: number;
  readonly totalAlkalinity: number;
  readonly flashPointTestMethod: 'none' | 'closed-cup' | 'open-cup';
  readonly containsAmmonia: boolean;
  readonly flashPoint: string;
  readonly phRange: string;
  readonly phLow: string;
  readonly phHigh: string;
  readonly phExact: string;

  // attachments step
  readonly labAnalyticalAttachments: LabAnalyticalAttachment[];
  readonly safetyDataSheets: SafetyDataSheet[];
  readonly additionalSupplements: AdditionalSupplement[];

  // chemical  composition step
  readonly constituents: ChemicalConstituent[];
  readonly compositionDeterminedBy: CompositionTypes[];

  // chemical properties step
  readonly chemicalProperties: {
    readonly arsenicPpm: number;
    readonly bariumPpm: number;
    readonly cadmiumPpm: number;
    readonly chromiumPpm: number;
    readonly leadPpm: number;
    readonly mercuryPpm: number;
    readonly seleniumPpm: number;
    readonly silverPpm: number;
    readonly tphPpm: number;
    readonly antimonyPpm: number;
    readonly berylliumPpm: number;
    readonly nickelPpm: number;
    readonly chromiumIVPpm: number;
    readonly cobaltPpm: number;
    readonly copperPpm: number;
    readonly molybdenumPpm: number;
    readonly thalliumPpm: number;
    readonly vanadiumPpm: number;
    readonly zincPpm: number;
    readonly bromidePpm: number;
    readonly calciumPpm: number;
    readonly chloridePpm: number;
    readonly fluoridePpm: number;
    readonly iodinePpm: number;
    readonly ironPpm: number;
    readonly magnesiumPpm: number;
    readonly potassiumPpm: number;
    readonly sodiumPpm: number;
    readonly sulfurPpm: number;
  };

  // chemical certifications step
  readonly chemicalCertifications: {
    readonly pcbPpm: number;
    readonly benzenePpm: number;
    readonly dioxinsPpm: number;
    readonly sulfidesPpm: number;
    readonly cyanidesPpm: number;
    readonly oxidizerPpm: number;
    readonly seleniumPpm: number;
    readonly silverPpm: number;
    readonly hasPathogens: boolean;
    readonly pathogensDescription: string;
    readonly hasPesticides: boolean;
    readonly pesticidesDescription: string;
    readonly hasPolymerizes: boolean;
    readonly polymerizesDescription: string;
    readonly hasRadioactives: boolean;
    readonly radioactivesDescription: string;
    readonly hasWaterReactives: boolean;
    readonly waterReactivesDescription: string;
    readonly hasOtherReactives: boolean;
    readonly otherReactivesDescription: string;
    readonly hasPentachlorophenol: boolean;
    readonly pentachlorophenolDescription: string;
    readonly hasOSHACarcinogen: boolean;
    readonly oshaCarcinogenDescription: string;
  };

  static readonly isDeepWell = (
    wasteProfile: WasteProfile
  ): wasteProfile is DeepwellWasteProfile => wasteProfile.type === 'deepwell';

  // In here but commented for future use
  // constructor(config?: DeepwellWasteProfile) {
  //   super(config);

  //   this.flashPoint = config
  //     ? DomainModelHelper.ensureDataIsNumber(config.flashPoint)
  //     : null;
  //   this.phLow = config
  //     ? DomainModelHelper.ensureDataIsNumber(config.phLow)
  //     : null;
  //   this.phHigh = config
  //     ? DomainModelHelper.ensureDataIsNumber(config.phHigh)
  //     : null;
  //   this.phExact = config
  //     ? DomainModelHelper.ensureDataIsNumber(config.phExact)
  //     : null;
  // }
}
